import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, BadgeCheck } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import InsuranceProviders from "@/components/sections/InsuranceProviders";
import useSEO from "@/hooks/useSEO";

const PROVIDERS = [
  "GreenShield", "Sun Life", "Manulife", "Canada Life", "Desjardins", "Blue Cross", "Empire Life", "Union Benefits", "Other / not sure",
];

const InsurancePage = () => {
  useSEO({
    title: "Verify My Rehab Benefits — Toronto | Insurance Coverage for Addiction Treatment",
    description:
      "Verify your addiction-treatment insurance coverage in 60 seconds. GreenShield, Sun Life, Manulife, Canada Life, Desjardins, Empire Life and union plans welcome. Confidential, no obligation.",
    keywords: "verify rehab insurance Toronto, GreenShield rehab coverage, Sun Life addiction treatment, Manulife inpatient rehab, insurance for rehab Ontario",
    canonical: "https://newbeginningsrecovery.ca/verify-benefits",
  });
  const [form, setForm] = useState({ name: "", phone: "", email: "", insurance_provider: "", member_id: "", dob: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number so we can verify your coverage.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        email: form.email.trim() || null,
        insurance_provider: form.insurance_provider || "Not specified",
        program_interest: "Insurance / coverage check",
        source: "insurance_check",
        message: [
          form.dob ? `DOB: ${form.dob}` : null,
          form.member_id ? `Member/policy ref: ${form.member_id}` : null,
        ].filter(Boolean).join(" · "),
        consent: true,
      });
      setDone(true);
      toast.success("Thank you — our team will verify your coverage and call you back.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="insurance-page" className="bg-sand min-h-screen">
      <Navbar />
      <section className="pt-40 md:pt-48 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-terracotta mb-4">Insurance Verification</p>
            <h1 className="font-serif text-5xl md:text-7xl text-forest tracking-tight leading-[0.98]">
              Verify My <span className="italic text-terracotta">Benefits</span>
            </h1>
            <p className="mt-6 text-lg text-ink2 leading-relaxed max-w-lg">
              We accept private pay and most insurance plans, including GreenShield. Share your insurance details
              and our admissions team will verify your coverage with your provider and contact you directly —
              confidentially and with no obligation.
            </p>
            <div className="mt-8 space-y-3">
              {["Private pay welcome", "Most insurance plans accepted (incl. GreenShield)", "Confidential, no-obligation verification"].map((t) => (
                <div key={t} className="flex items-center gap-3">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-sage/25 text-forest shrink-0"><BadgeCheck className="w-4 h-4" /></span>
                  <span className="text-ink font-medium">{t}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            {done ? (
              <div data-testid="insurance-success" className="rounded-3xl bg-white border border-line shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-10 text-center">
                <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-sage/20 text-forest"><CheckCircle2 className="w-8 h-8" /></div>
                <h3 className="mt-6 font-serif text-3xl text-forest">We’re on it</h3>
                <p className="mt-3 text-ink2 leading-relaxed">
                  Our team will verify your coverage and reach out very soon — usually within the hour. You’ve taken a brave first step.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="insurance-form" className="rounded-3xl bg-white border border-line shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-6 md:p-9">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-ink font-medium">Your name *</Label>
                    <Input data-testid="insurance-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" placeholder="First and last name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-ink font-medium">Phone *</Label>
                    <Input data-testid="insurance-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" placeholder="(647) 000-0000" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-ink font-medium">Email</Label>
                    <Input data-testid="insurance-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" placeholder="you@email.com" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-ink font-medium">Insurance provider</Label>
                    <Select value={form.insurance_provider} onValueChange={(v) => update("insurance_provider", v)}>
                      <SelectTrigger data-testid="insurance-provider" className="h-12 rounded-xl border-line bg-sand/40"><SelectValue placeholder="Select provider" /></SelectTrigger>
                      <SelectContent>
                        {PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-ink font-medium">Date of birth</Label>
                    <Input
                      data-testid="insurance-dob"
                      type="date"
                      value={form.dob}
                      onChange={(e) => update("dob", e.target.value)}
                      className="h-12 rounded-xl border-line bg-sand/40"
                      max={new Date().toISOString().slice(0, 10)}
                    />
                    <p className="text-[11px] text-ink2/70">Required by most insurers to verify your policy</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-ink font-medium">Member / policy number (optional)</Label>
                    <Input data-testid="insurance-member" value={form.member_id} onChange={(e) => update("member_id", e.target.value)} className="h-12 rounded-xl border-line bg-sand/40" placeholder="If you have it handy" />
                  </div>
                </div>
                <Button type="submit" data-testid="insurance-submit" disabled={loading} className="mt-6 w-full h-14 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white text-base font-semibold">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify my coverage"}
                </Button>
                <p className="mt-4 flex items-center justify-center gap-2 text-xs text-ink2">
                  <ShieldCheck className="w-4 h-4 text-sage" /> 100% confidential · No obligation
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
      <InsuranceProviders compact />
      <Footer />
    </div>
  );
};

export default InsurancePage;
